import React, { useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Widget } from "@/types/dashboard.types";
import { useCreateWidget, useUpdateWidget } from "@/hooks";
import { toast } from "sonner";
import { Loader2, Database } from "lucide-react";

interface CreateWidgetGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardId: string;
  availableWidgets: Widget[];
  onCreated?: (widget: any) => void;
}

export default function CreateWidgetGroupModal({
  isOpen,
  onClose,
  dashboardId,
  availableWidgets,
  onCreated,
}: CreateWidgetGroupModalProps) {
  const { t } = useTranslation();
  const [groupName, setGroupName] = useState("");
  const [selectedWidgetIds, setSelectedWidgetIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createWidgetMutation = useCreateWidget();
  const updateWidgetMutation = useUpdateWidget();

  // Filter out widgets that are already in groups
  const availableGroupWidgets = useMemo(() => {
    return availableWidgets.filter(widget =>
      widget.visualizationType !== "group" &&
      !availableWidgets.some(w =>
        w.visualizationType === "group" &&
        ((w as any).config as any)?.group?.childrenIds?.includes(widget.id)
      )
    );
  }, [availableWidgets]);

  const handleWidgetToggle = (widgetId: string, checked: boolean) => {
    setSelectedWidgetIds(prev =>
      checked
        ? [...prev, widgetId]
        : prev.filter(id => id !== widgetId)
    );
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!groupName.trim()) {
      newErrors.groupName = t('processManagement.groupNameRequired', 'Group name is required');
    } else if (groupName.length < 3) {
      newErrors.groupName = t('processManagement.groupNameMustBeAtLeast3Characters', 'Group name must be at least 3 characters');
    }

    if (selectedWidgetIds.length < 2) {
      newErrors.widgets = t('processManagement.selectAtLeast2Widgets', 'Select at least 2 widgets for the group');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // 1) Create minimal group widget (backend requires dateRange)
      const createRes = await createWidgetMutation.mutateAsync({
        dashboardId,
        title: groupName.trim(),
        visualizationType: "group",
        dateRange: { preset: "all" },
      });

      const created =
        (createRes as any)?.data?.data ??
        (createRes as any)?.data ??
        createRes;

      if (!created?.id) {
        throw new Error("Failed to create group widget (no id returned)");
      }

      // 2) Immediately patch config to attach childrenIds (backend update supports direct config)
      const updateRes = await updateWidgetMutation.mutateAsync({
        id: created.id,
        formData: {
          config: {
            group: {
              childrenIds: selectedWidgetIds,
            },
          },
        },
      });

      const updated =
        (updateRes as any)?.data?.data ??
        (updateRes as any)?.data ??
        updateRes ??
        created;

      // 3) Notify parent so it can add group to local state/layout immediately
      onCreated?.(updated);

      toast.success(t('processManagement.groupCreatedSuccessfully'));
      handleClose();
    } catch (error) {
      toast.error(t('processManagement.failedToCreateGroup'));
      console.error("Create widget group error:", error);
    }
  };

  const handleClose = () => {
    setGroupName("");
    setSelectedWidgetIds([]);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('processManagement.createWidgetGroup')}</DialogTitle>
          <DialogDescription>
            {t('processManagement.createWidgetGroupDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Group Name */}
          <div className="space-y-2">
            <Label htmlFor="groupName">{t('processManagement.groupName')} *</Label>
            <Input
              id="groupName"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t('processManagement.groupNamePlaceholder')}
              className={errors.groupName ? "border-red-500" : ""}
            />
            {errors.groupName && (
              <p className="text-sm text-red-600">{errors.groupName}</p>
            )}
          </div>

          {/* Widget Selection */}
          <div className="space-y-3">
            <Label>{t('processManagement.selectWidgetsForGroup')} *</Label>
            <p className="text-sm text-gray-600">
              {t('processManagement.selectAtLeast2WidgetsForGroup')}
            </p>

            {availableGroupWidgets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Database className="w-8 h-8 mx-auto mb-2" />
                <p>{t('processManagement.noWidgetsAvailableForGroup')}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                {availableGroupWidgets.map((widget) => (
                  <div key={widget.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={widget.id}
                      checked={selectedWidgetIds.includes(widget.id)}
                      onCheckedChange={(checked) =>
                        handleWidgetToggle(widget.id, !!checked)
                      }
                    />
                    <label
                      htmlFor={widget.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                    >
                      <Database className="w-4 h-4" />
                      {widget.title || t('processManagement.untitledWidget')}
                      <span className="text-xs text-gray-500 capitalize">
                        ({widget.visualizationType})
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {errors.widgets && (
              <p className="text-sm text-red-600">{errors.widgets}</p>
            )}

            {selectedWidgetIds.length > 0 && (
              <div className="text-sm text-gray-600">
                {t('processManagement.widgetsSelected', '{{count}} widgets selected', {
                  count: selectedWidgetIds.length
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={createWidgetMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createWidgetMutation.isPending || availableGroupWidgets.length < 2}
            className="main-dark-button"
          >
            {createWidgetMutation.isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            {createWidgetMutation.isPending
              ? t('common.creating')
              : t('processManagement.createWidgetGroup')
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
